import { CommonModule } from '@angular/common';
import { Component, HostListener, ElementRef, ViewChild, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { AlertService } from '../../../utils/swal-fire';
import { environment } from '../../../../environments/environment';
import { PAGINAS_DISPONIBLES } from '../../../core/config/permisos-paginas2';
@Component({
    selector: 'app-company-selector', standalone: true, imports: [CommonModule, FormsModule, MatIconModule],
    templateUrl: './company-selector.component.html', styleUrls: ['./company-selector.component.css'],
})
export class CompanySelectorComponent implements OnInit {
    @ViewChild('companyDialog', { static: true }) companyDialog!: ElementRef<HTMLDialogElement>;
    open = false;
    busy = false;
    error = '';
    companies: any[] = [];
    editing: any = null;
    creating = false;
    tab: 'details' | 'members' | 'projects' = 'details';
    form: any = {};
    members: any[] = [];
    permissions: any[] = [];
    projects: any[] = [];
    selectedProjects: number[] = [];
    memberEmail = '';
    memberEnabled = true;
    memberPermissions: number[] = [];
    private permissionSelectionSource: number[] | null = null;
    private permissionSelection = new Set<number>();
    search = '';
    accounts: any[] = [];
    accountSearchDone = false;
    selectedAccount: any = null;
    creatingUser = false;
    newUser: any = {};
    permissionTab: 'pages' | 'actions' = 'pages';
    permissionSearch = '';
    expandedSections: Record<string,boolean> = {};
    selectedPageRoutes: string[] = [];
    projectSearch = '';
    private base = `${environment.apiUrl}/config/companies`;
    constructor(private http: HttpClient, public auth: AuthService, private alertService: AlertService) { }
    ngOnInit(): void { void this.run(() => this.load()); }
    get hasMultipleCompanies(): boolean { return this.companies.filter(company => company.enabled).length > 1; }
    get companyName(): string { return this.auth.getUsuarioDesdeToken()?.company_name || 'Empresas'; }
    get canCreate(): boolean { return this.auth.getPermisos().includes('/api/config/companies/manage'); }
    get visibleProjects(): any[] { const q = this.projectSearch.toLowerCase(); return this.projects.filter(p => `${p.codigo} ${p.nombre}`.toLowerCase().includes(q)); }
    @HostListener('window:storage', ['$event'])
    onStorage(event: StorageEvent): void {
        if (event.key === 'token')
            window.location.reload();
    }
    async run(action: () => Promise<void>): Promise<void> {
        if (this.busy)
            return;
        this.busy = true;
        this.error = '';
        try {
            await action();
        }
        catch (e: any) {
            this.error = e?.error?.error || e?.error?.message || 'No se pudo completar la operación.';
        }
        finally {
            this.busy = false;
        }
    }
    close(): void { if(this.busy)return;this.companyDialog.nativeElement.close();this.open=false; }
    async show(): Promise<void> { this.open = true; this.companyDialog.nativeElement.showModal(); this.editing = null; this.creating = false; await this.run(() => this.load()); }
    private async load(): Promise<void> { const result: any = await firstValueFrom(this.http.get(`${this.base}/mine`)); this.companies = result.data; }
    async switchCompany(company: any): Promise<void> {
        if (company.current || !company.enabled || this.busy)
            return;
        if (!window.confirm(`Cambiar a ${company.name}. Se cerrará la pantalla actual; guarda antes tus cambios pendientes.`))
            return;
        await this.run(async () => {
            const result: any = await firstValueFrom(this.http.post(`${this.base}/switch`, { tenant_id: company.id }));
            this.auth.applyCompanySession(result.token, result.permisos);
            window.location.assign('/recursos-humanos/attendance');
        });
    }
    create(): void { this.creating = true; this.editing = null; this.form = { name: '', slug: '', ruc: '', email: '', phone: '', address: '', enabled: true }; this.tab = 'details'; this.error = ''; }
    async edit(company: any): Promise<void> {
        await this.run(async () => { const result: any = await firstValueFrom(this.http.get(`${this.base}/${company.id}`)); this.editing = result.data; this.form = { ...result.data }; this.creating = false; this.tab = 'details'; });
    }
    async save(): Promise<void> {
        await this.run(async () => {
            const wasCreating = this.creating;
            const keys = ['name', 'slug', 'ruc', 'email', 'phone', 'address', 'city', 'general_manager_name', 'general_manager_identification', 'general_manager_email', 'general_manager_phone', 'enabled'];
            const body = Object.fromEntries(keys.filter(k => this.form[k] !== undefined && this.form[k] !== null).map(k => [k, this.form[k]]));
            if (this.creating) body['enabled'] = true;
            const result: any = await firstValueFrom(this.creating ? this.http.post(this.base, body) : this.http.patch(`${this.base}/${this.editing.id}`, body));
            this.editing = wasCreating ? null : result.data;
            this.creating = false;
            this.form = wasCreating ? {} : { ...result.data };
            this.alertService.toast(wasCreating ? 'Empresa creada correctamente' : 'Datos de la empresa actualizados', 'success', this.companyDialog.nativeElement);
            await this.load();
        });
    }
    async showMembers(): Promise<void> { await this.run(async () => { this.tab = 'members'; await this.loadMembers(); this.resetMember(); }); }
    private async loadMembers(): Promise<void> { const result: any = await firstValueFrom(this.http.get(`${this.base}/${this.editing.id}/members`)); this.members = result.data; this.permissions = result.permissions; }
    resetMember(): void { this.memberEmail = ''; this.memberEnabled = true; this.memberPermissions = []; this.selectedAccount=null;this.creatingUser=false;this.newUser={};this.accounts=[];this.search='';this.accountSearchDone=false;this.selectedPageRoutes=[];this.expandedSections={};this.permissionSearch='';this.permissionTab='pages'; }
    selectMember(member: any): void { this.selectedAccount=member;this.creatingUser=false;this.memberEmail = member.correo; this.memberEnabled = member.enabled; this.memberPermissions = [...member.permission_ids];this.selectedPageRoutes=this.permissions.filter(permission=>this.memberPermissions.includes(permission.id)&&this.pageRoutes.has(permission.route)).map(permission=>permission.route);this.permissionTab='pages';this.permissionSearch='';this.expandedSections={}; }
    selectAccount(account:any):void {
        const existing=this.members.find(member=>member.correo===account.correo);
        this.selectMember(existing||{...account,enabled:true,permission_ids:[]});
        this.accounts=[];this.search='';this.accountSearchDone=false;
    }
    async searchAccounts():Promise<void> {
        await this.run(async()=>{const result:any=await firstValueFrom(this.http.get(`${this.base}/${this.editing.id}/accounts`,{params:{search:this.search}}));this.accounts=result.data;this.accountSearchDone=true;});
    }
    startNewUser():void {this.resetMember();this.creatingUser=true;}
    async createUser():Promise<void> {
        await this.run(async()=>{
            const email=this.newUser.email;
            await firstValueFrom(this.http.post(`${this.base}/${this.editing.id}/users`,this.newUser));
            this.newUser={};this.creatingUser=false;
            this.alertService.toast('Usuario creado en '+this.editing.name,'success',this.companyDialog.nativeElement);
            await this.loadMembers();
            const member=this.members.find(item=>item.correo===email.trim());
            if(member)this.selectMember(member);
        });
    }
    get permissionSections():any[] {
        const sections=new Map<number,any>();
        for(const permission of this.permissions){
            if(this.pageRoutes.has(permission.route))continue;
            if(this.permissionSearch&&!`${permission.action} ${permission.route} ${permission.header.name} ${permission.header.category||''}`.toLowerCase().includes(this.permissionSearch.toLowerCase()))continue;
            const header=permission.header;
            if(!sections.has(header.id))sections.set(header.id,{id:header.id,name:header.name,category:header.category,domain:header.domain,permissions:[]});
            sections.get(header.id).permissions.push(permission);
        }
        return [...sections.values()].sort((a,b)=>`${a.domain} ${a.category} ${a.name}`.localeCompare(`${b.domain} ${b.category} ${b.name}`));
    }
    readonly pageRoutes=new Set(PAGINAS_DISPONIBLES.flatMap(group=>group.paginas.map(page=>page.ruta)));
    get pageSections():any[] {
        const query=this.permissionSearch.toLocaleLowerCase('es');
        return PAGINAS_DISPONIBLES.map((group,index)=>({id:'pages-'+index,name:group.categoria,permissions:group.paginas.filter(page=>!query||`${group.categoria} ${page.nombre} ${page.ruta}`.toLocaleLowerCase('es').includes(query))})).filter(group=>group.permissions.length);
    }
    selectedPagesInSection(section:any):number {return section.permissions.filter((page:any)=>this.selectedPageRoutes.includes(page.ruta)).length;}
    togglePage(route:string,checked:boolean):void {this.selectedPageRoutes=checked?[...new Set([...this.selectedPageRoutes,route])]:this.selectedPageRoutes.filter(value=>value!==route);}
    togglePageSection(section:any,checked:boolean):void {
        const routes=section.permissions.map((page:any)=>page.ruta);
        this.selectedPageRoutes=checked?[...new Set([...this.selectedPageRoutes,...routes])]:this.selectedPageRoutes.filter(route=>!routes.includes(route));
    }
    selectVisiblePermissions(checked:boolean):void {
        if(this.permissionTab==='pages')for(const section of this.pageSections)this.togglePageSection(section,checked);
        else for(const section of this.permissionSections)this.toggleSection(section,checked);
    }
    expandPermissions(expanded:boolean):void {for(const section of (this.permissionTab==='pages'?this.pageSections:this.permissionSections))this.expandedSections[this.permissionTab+section.id]=expanded;}
    pagePayload():any[] {return PAGINAS_DISPONIBLES.flatMap(group=>group.paginas.filter(page=>this.selectedPageRoutes.includes(page.ruta)).map(page=>({route:page.ruta,name:page.nombre,category:group.categoria})));}
    hasPermission(id:number):boolean {
        if(this.permissionSelectionSource!==this.memberPermissions){this.permissionSelectionSource=this.memberPermissions;this.permissionSelection=new Set(this.memberPermissions);}
        return this.permissionSelection.has(id);
    }
    trackPermissionSection(_index:number,section:any):number {return section.id;}
    selectedInSection(section:any):number {return section.permissions.filter((permission:any)=>this.hasPermission(permission.id)).length;}
    toggleSection(section:any,checked:boolean):void {
        const ids=section.permissions.map((permission:any)=>permission.id);
        this.memberPermissions=checked?[...new Set([...this.memberPermissions,...ids])]:this.memberPermissions.filter(id=>!ids.includes(id));
    }
    toggleAllPermissions(checked:boolean):void {this.memberPermissions=checked?this.permissions.map(permission=>permission.id):[];}
    togglePermission(id: number, checked: boolean): void { this.memberPermissions = checked ? [...new Set([...this.memberPermissions, id])] : this.memberPermissions.filter(p => p !== id); }
    async saveMember(): Promise<void> {
        if(!this.selectedAccount)return;
        await this.run(async () => { await firstValueFrom(this.http.post(`${this.base}/${this.editing.id}/members`, { email: this.memberEmail, enabled: this.memberEnabled, permission_ids: this.memberPermissions.filter(id=>!this.permissions.some(permission=>permission.id===id&&this.pageRoutes.has(permission.route))), page_permissions:this.pagePayload() }));this.alertService.toast('Usuario y permisos guardados en '+this.editing.name,'success',this.companyDialog.nativeElement); await this.loadMembers(); this.resetMember(); });
    }
    async showProjects(): Promise<void> { await this.run(async () => { const result: any = await firstValueFrom(this.http.get(`${this.base}/${this.editing.id}/projects`)); this.tab = 'projects'; this.projects = result.data; this.selectedProjects = result.selected; }); }
    toggleProject(id: number, checked: boolean): void { this.selectedProjects = checked ? [...this.selectedProjects, id] : this.selectedProjects.filter(p => p !== id); }
    async saveProjects(): Promise<void> { await this.run(async () => { await firstValueFrom(this.http.put(`${this.base}/${this.editing.id}/projects`, { project_ids: this.selectedProjects })); }); }
}
